import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, User, Calendar, MapPin, Package, Building2, ArrowRight, Users } from 'lucide-react';
import { List, AutoSizer, WindowScroller, CellMeasurerCache, CellMeasurer } from 'react-virtualized';
import { PilgrimSelectorDocument } from '../../../types/pilgrimSelector';
import { LoadingProgress } from '../../common/LoadingProgress';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { useLanguage } from '../../../contexts/LanguageContext';
import { checkDateOverlap, calculateAge } from '../../../utils/dateUtils';

type Props = {
  onSelect: (pilgrim: any) => void;
  roomType: string;
  hotelName: string;
  bedIndex: number;
  currentAssignments: any[];
  dateConstraints: {
    oldestCheckIn: number;
    newestCheckOut: number;
  } | null;
};

// Create a cache outside component to prevent re-creating on every render
const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 100,
  keyMapper: (index) => index,
});

export const PilgrimSelector = ({
  onSelect,
  roomType,
  hotelName,
  bedIndex,
  currentAssignments,
  dateConstraints
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [pilgrims, setPilgrims] = useState<any[]>([]);
  const [hasInitialData, setHasInitialData] = useState(false);
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const { translations } = useLanguage();
  const [listKey, setListKey] = useState(0); // Key to force List component remount when needed

  // Reset cache when room type or hotel changes
  useEffect(() => {
    cache.clearAll();
    setListKey(prev => prev + 1);
  }, [roomType, hotelName, bedIndex]);

  // Filter pilgrims based on search - MOVED THIS UP BEFORE USING IT
  const filteredPilgrims = useMemo(() => {
    return pilgrims.filter(pilgrim => {
      if (!searchTerm) return true;
      
      return (
        pilgrim.pilgrimData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(pilgrim.pilgrimData.passportNumber).toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilgrim.pilgrimData.residenceCountry.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilgrim.packageData.packageName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [pilgrims, searchTerm]);

  // Desktop row component - memoized to prevent unnecessary re-renders
  const DesktopRow = useCallback(({ index, key, style, parent }: any) => {
    const pilgrim = filteredPilgrims[index];
    if (!pilgrim) return null;

    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}
      >
        {({ registerChild }) => (
          <div
            ref={registerChild as any}
            style={style}
            className="p-2 cursor-pointer hover:bg-blue-50/80 dark:hover:bg-blue-900/20 border-b last:border-b-0"
            onClick={() => onSelect(pilgrim)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${
                  pilgrim.pilgrimData.gender === 'male' ? 'bg-blue-100' : 'bg-pink-100'
                }`}>
                  <User className={pilgrim.pilgrimData.gender === 'male' ? 'text-blue-500' : 'text-pink-500'} size={14} />
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate text-sm">{pilgrim.pilgrimData.name}</div>
                  <div className="text-xs text-gray-600">
                    {translations?.pilgrimSelector?.passport || 'Passport'}: {pilgrim.pilgrimData.passportNumber}
                  </div>
                </div>
                {pilgrim.pilgrimData.dateOfBirth && (
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] text-white ${
                    calculateAge(pilgrim.pilgrimData.dateOfBirth) > 60 ? 'bg-red-500' :
                    calculateAge(pilgrim.pilgrimData.dateOfBirth) > 40 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}>
                    {calculateAge(pilgrim.pilgrimData.dateOfBirth)} {translations?.pilgrimCard?.years || 'years'}
                  </span>
                )}
              </div>
            </div>

            {/* Secondary Info */}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar size={12} />
                <span>{translations?.pilgrimSelector?.checkIn || 'Check-in'}: {getHotelDates(pilgrim, hotelName).startDate}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar size={12} />
                <span>{translations?.pilgrimSelector?.checkOut || 'Check-out'}: {getHotelDates(pilgrim, hotelName).endDate}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin size={12} />
                <span>{pilgrim.pilgrimData.residenceCountry}</span>
              </div>
              <div className="flex items-center gap-1">
                <Package size={12} />
                <span>{pilgrim.packageData.packageName}</span>
              </div>
              <div className="flex items-center gap-1">
                <span>{pilgrim.guideData.name}</span>
                <ArrowRight size={12} className="mx-0.5" />
                <span>{pilgrim.organizerData.name}</span>
              </div>
            </div>
          </div>
        )}
      </CellMeasurer>
    );
  }, [filteredPilgrims, onSelect, translations, hotelName]);

  // Mobile row component - memoized to prevent unnecessary re-renders
  const MobileRow = useCallback(({ index, key, style, parent }: any) => {
    const pilgrim = filteredPilgrims[index];
    if (!pilgrim) return null;

    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}
      >
        {({ registerChild }) => (
          <div
            ref={registerChild as any}
            style={style}
            className="p-4 cursor-pointer bg-white shadow-sm rounded-lg mx-2 mb-2"
            onClick={() => onSelect(pilgrim)}
          >
            <div className="flex flex-col space-y-2">
              {/* Main Info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`p-2 rounded-full ${
                    pilgrim.pilgrimData.gender === 'male' ? 'bg-blue-100' : 'bg-pink-100'
                  }`}>
                    <User className={pilgrim.pilgrimData.gender === 'male' ? 'text-blue-500' : 'text-pink-500'} size={18} />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="font-medium text-base truncate">{pilgrim.pilgrimData.name}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <MapPin size={14} />
                      <span>{pilgrim.pilgrimData.residenceCountry}</span>
                    </div>
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <Package size={14} />
                      <span>{pilgrim.packageData.packageName}</span>
                    </div>
                  </div>
                </div>
                <div className="shrink-0 ml-2">
                  {pilgrim.pilgrimData.dateOfBirth && (
                    <span className={`shrink-0 px-2 py-1 rounded-full text-xs font-medium text-white ${
                      calculateAge(pilgrim.pilgrimData.dateOfBirth) > 60 ? 'bg-red-500' :
                      calculateAge(pilgrim.pilgrimData.dateOfBirth) > 40 ? 'bg-yellow-500' :
                      'bg-green-500'
                    }`}>
                      {calculateAge(pilgrim.pilgrimData.dateOfBirth)} {translations?.pilgrimCard?.years || 'years'}
                    </span>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 gap-2">
                <div className="flex flex-col gap-1 text-gray-600 bg-gray-50 p-2 rounded-lg">
                  <Calendar size={14} className="shrink-0" />
                  <span className="whitespace-nowrap text-xs">
                    {translations?.pilgrimSelector?.checkIn || 'Check-in'}: {getHotelDates(pilgrim, hotelName).startDate || '-'}
                  </span>
                  <span className="whitespace-nowrap text-xs">
                    {translations?.pilgrimSelector?.checkOut || 'Check-out'}: {getHotelDates(pilgrim, hotelName).endDate || '-'}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-600 bg-gray-50 p-2 rounded-lg overflow-hidden">
                  <MapPin size={14} className="shrink-0" />
                  <span className="truncate text-xs">{pilgrim.pilgrimData.residenceCountry}</span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-600 bg-gray-50 p-2 rounded-lg overflow-hidden">
                  <Package size={14} className="shrink-0" />
                  <span className="truncate text-xs">{pilgrim.packageData.packageName}</span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-600 bg-gray-50 p-2 rounded-lg overflow-hidden">
                  <User size={14} className="shrink-0" />
                  <span className="truncate text-xs">{pilgrim.guideData.name}</span>
                  <ArrowRight size={12} className="shrink-0" />
                  <span className="truncate text-xs">{pilgrim.organizerData.name}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CellMeasurer>
    );
  }, [filteredPilgrims, onSelect, translations, hotelName]);

  // Keep all the existing useEffect and filtering logic
  useEffect(() => {
    const fetchPilgrims = async () => {
      setIsLoading(true);
      try {
        console.log('Starting pilgrim fetch with params:', {
          roomType,
          hotelName,
          userRole,
          userId,
          dateConstraints,
          currentAssignments: currentAssignments.map(a => ({
            checkIn: typeof a.ci === 'string' ? a.ci : new Date(a.ci).toISOString(),
            checkOut: typeof a.co === 'string' ? a.co : new Date(a.co).toISOString()
          }))
        });

        // Base query for PilgrimSelector
        let baseQuery = query(
          collection(firestore, 'PilgrimSelector'),
          where('status', '==', 'active')
        );

        // Add role-specific filters
        if (userRole === 'guide' && userId) {
          baseQuery = query(
            collection(firestore, 'PilgrimSelector'),
            where('status', '==', 'active'),
            where('guideId', '==', userId)
          );
        } else if (userRole === 'organizer' && userId) {
          baseQuery = query(
            collection(firestore, 'PilgrimSelector'),
            where('status', '==', 'active'),
            where('organizerId', '==', userId)
          );
        }

        const snapshot = await getDocs(baseQuery);
        console.log('Total pilgrims found:', snapshot.size);
        setProgress({ current: 0, total: snapshot.size });

        // Filter pilgrims based on hotel and room type
        const eligiblePilgrims = snapshot.docs
          .map(doc => ({ ...doc.data(), id: doc.id }) as PilgrimSelectorDocument)
          .filter(pilgrim => {
            console.log('\nProcessing pilgrim:', {
              id: pilgrim.id,
              name: pilgrim.pilgrimData.name,
              isAccommodated: pilgrim.isAccommodated,
              hotelName: hotelName
            });

            // Skip if already accommodated in this hotel
            if (pilgrim.isAccommodated?.[hotelName]) {
              console.log('❌ Pilgrim filtered out: Already accommodated in this hotel');
              return false;
            }

            // Convert room type to match package data format
            const roomTypeMap = {
              'd': 'double',
              't': 'triple',
              'q': 'quadruple'
            };
            const fullRoomType = roomTypeMap[roomType as keyof typeof roomTypeMap];

            // Match hotel and room type
            const matchesMainMakkah = 
              pilgrim.packageData.mainMakkah.hotel === hotelName && 
              pilgrim.packageData.mainMakkah.roomType === fullRoomType;

            const matchesShiftingMakkah = 
              pilgrim.packageData.shiftingMakkah?.hotel === hotelName && 
              pilgrim.packageData.shiftingMakkah?.roomType === fullRoomType;

            const matchesMadinah = 
              pilgrim.packageData.madinah.hotel === hotelName && 
              pilgrim.packageData.madinah.roomType === fullRoomType;

            console.log('Hotel and room type matching:', {
              requestedHotel: hotelName,
              requestedRoomType: fullRoomType,
              mainMakkah: {
                hotel: pilgrim.packageData.mainMakkah.hotel,
                roomType: pilgrim.packageData.mainMakkah.roomType,
                matches: matchesMainMakkah
              },
              shiftingMakkah: pilgrim.packageData.shiftingMakkah ? {
                hotel: pilgrim.packageData.shiftingMakkah.hotel,
                roomType: pilgrim.packageData.shiftingMakkah.roomType,
                matches: matchesShiftingMakkah
              } : 'Not applicable',
              madinah: {
                hotel: pilgrim.packageData.madinah.hotel,
                roomType: pilgrim.packageData.madinah.roomType,
                matches: matchesMadinah
              }
            });

            // Find start/end dates for the selected hotel
            let startDate = '';
            let endDate = '';
            
            if (matchesMainMakkah) {
              startDate = pilgrim.packageData.mainMakkah.startDate;
              endDate = pilgrim.packageData.mainMakkah.endDate;
            } else if (matchesShiftingMakkah) {
              startDate = pilgrim.packageData.shiftingMakkah?.startDate || '';
              endDate = pilgrim.packageData.shiftingMakkah?.endDate || '';
            } else if (matchesMadinah) {
              startDate = pilgrim.packageData.madinah.startDate;
              endDate = pilgrim.packageData.madinah.endDate;
            }

            // If no dates found, pilgrim doesn't match
            if (!startDate || !endDate) {
              console.log('❌ Pilgrim filtered out: No matching dates found for hotel');
              return false;
            }

            // If there are date constraints, check them
            if (dateConstraints) {
              console.log('Checking date constraints');
              // Convert DD/MM/YYYY to timestamp
              const [startDay, startMonth, startYear] = startDate.split('/').map(Number);
              const [endDay, endMonth, endYear] = endDate.split('/').map(Number);
              
              const pilgrimCheckIn = Date.UTC(startYear, startMonth - 1, startDay);
              const pilgrimCheckOut = Date.UTC(endYear, endMonth - 1, endDay);

              console.log('Checking date constraints:', {
                pilgrimStay: {
                  checkIn: new Date(pilgrimCheckIn).toISOString(),
                  checkOut: new Date(pilgrimCheckOut).toISOString()
                },
                currentAssignments: currentAssignments.map(a => ({
                  checkIn: typeof a.ci === 'string' ? 
                    a.ci : new Date(a.ci).toISOString(),
                  checkOut: typeof a.co === 'string' ? 
                    a.co : new Date(a.co).toISOString()
                }))
              });

              // Check for overlap - IMPORTANT: Ensure dates are in the right format
              let { hasOverlap, conflictingAssignment } = checkDateOverlap(
                pilgrimCheckIn,
                pilgrimCheckOut,
                currentAssignments
              );

              // If there's an overlap, try again with offset
              if (hasOverlap) {
                console.log('First attempt found overlap, trying with offset...', conflictingAssignment);
                
                const secondTry = checkDateOverlap(
                  pilgrimCheckIn,
                  pilgrimCheckOut,
                  currentAssignments,
                  true // Add one day offset for checking only, not for storage
                );

                // Update results with second try
                hasOverlap = secondTry.hasOverlap;
                if (hasOverlap) {
                  conflictingAssignment = secondTry.conflictingAssignment;
                }
              }

              if (hasOverlap) {
                console.log('❌ Date overlap found:', {
                  pilgrimStay: {
                    checkIn: new Date(pilgrimCheckIn).toISOString(),
                    checkOut: new Date(pilgrimCheckOut).toISOString()
                  },
                  conflictingAssignment
                });
                console.log('❌ Pilgrim filtered out: Date overlap with existing assignments');
                return false;
              }
            }

            console.log('✅ Pilgrim is eligible for accommodation');
            return matchesMainMakkah || matchesShiftingMakkah || matchesMadinah;
          })
          .sort((a, b) => {
            // Sort by arrival date (oldest first)
            const dateA = getHotelDates(a, hotelName).startDate ? 
              new Date(getHotelDates(a, hotelName).startDate.split('/').reverse().join('-')) : 
              new Date(0);
            const dateB = getHotelDates(b, hotelName).startDate ? 
              new Date(getHotelDates(b, hotelName).startDate.split('/').reverse().join('-')) : 
              new Date(0);
            
            if (dateA.getTime() !== dateB.getTime()) {
              return dateA.getTime() - dateB.getTime();
            }
            
            // Sort by package name
            if (a.packageData.packageName !== b.packageData.packageName) {
              return a.packageData.packageName.localeCompare(b.packageData.packageName);
            }
            
            // Sort by gender (females first)
            if (a.pilgrimData.gender !== b.pilgrimData.gender) {
              return a.pilgrimData.gender === 'female' ? -1 : 1;
            }
            
            // Sort by age (oldest first)
            const ageA = calculateAge(a.pilgrimData.dateOfBirth);
            const ageB = calculateAge(b.pilgrimData.dateOfBirth);
            return ageB - ageA;
          });

        console.log('Final eligible pilgrims count:', eligiblePilgrims.length);
        
        // After filtering is complete, clear the cache to ensure correct measurements
        cache.clearAll();
        
        setPilgrims(eligiblePilgrims);
        setHasInitialData(true);
      } catch (error) {
        console.error('Error fetching pilgrims:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPilgrims();
  }, [hotelName, roomType, userRole, userId, dateConstraints, currentAssignments]);

  // When filteredPilgrims change, clear cache to recalculate row heights
  useEffect(() => {
    cache.clearAll();
    setListKey(prev => prev + 1);  // Force List component to remount
  }, [filteredPilgrims.length]);

  return (
    <div className="space-y-4">
      {/* Search Input and Count */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder={translations?.pilgrimSelector?.searchPlaceholder || 'Search by name, passport, package...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        </div>
        
        {/* Pilgrim Count */}
        <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-lg">
          <Users size={18} className="text-primary-500" />
          <span className="text-sm font-medium text-primary-700">
            {filteredPilgrims.length} {translations?.pilgrimSelector?.foundPilgrims || 'pilgrims found'}
          </span>
        </div>
      </div>

      {/* Loading Progress */}
      {isLoading && progress.total > 0 && (
        <LoadingProgress
          progress={progress.current}
          total={progress.total}
          label={translations?.pilgrimSelector?.loadingPilgrims || 'Loading pilgrims...'}
        />
      )}

      {/* Pilgrims List - With optimized virtualization and smooth scrolling */}
      <div 
        className="border rounded-lg overflow-hidden bg-gray-50" 
        style={{ height: '400px' }}
      >
        {isLoading ? (
          <div className="p-4 h-full flex items-center justify-center bg-gray-50">
            <LoadingSpinner />
          </div>
        ) : filteredPilgrims.length > 0 ? (
          <div className="h-full will-change-transform" style={{ contain: 'paint' }}>
            <AutoSizer>
              {({ width, height }) => (
                <List
                  key={listKey}
                  className="pilgrim-list"
                  width={width}
                  height={height}
                  deferredMeasurementCache={cache}
                  rowCount={filteredPilgrims.length}
                  rowHeight={cache.rowHeight}
                  rowRenderer={window.innerWidth >= 768 ? DesktopRow : MobileRow}
                  overscanRowCount={500}
                  style={{ willChange: 'transform', contain: 'paint' }}
                  containerStyle={{ backgroundColor: '#f9fafb', contain: 'paint' }}
                />
              )}
            </AutoSizer>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 h-full flex items-center justify-center bg-gray-50">
            {hasInitialData ? (
              searchTerm ? 
                translations?.pilgrimSelector?.noResults || 'No matching pilgrims found'
                : translations?.pilgrimSelector?.noPilgrims || 'No eligible pilgrims found'
            ) : (
              translations?.pilgrimSelector?.startSearch || 'Start searching for a pilgrim...'
            )}
          </div>
        )}
      </div>
      
      <style jsx="true">{`
        .pilgrim-list {
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
          overscroll-behavior: none;
          background-color: #f9fafb;
          contain: paint;
          will-change: transform;
        }
      `}</style>
    </div>
  );
};

// Helper function to get hotel dates based on the target hotel
const getHotelDates = (pilgrim: PilgrimSelectorDocument, hotelName: string) => {
  if (hotelName === pilgrim.packageData.mainMakkah.hotel) {
    return {
      startDate: pilgrim.packageData.mainMakkah.startDate,
      endDate: pilgrim.packageData.mainMakkah.endDate
    };
  }
  if (pilgrim.packageData.shiftingMakkah?.hotel === hotelName) {
    return {
      startDate: pilgrim.packageData.shiftingMakkah.startDate,
      endDate: pilgrim.packageData.shiftingMakkah.endDate
    };
  }
  if (pilgrim.packageData.madinah.hotel === hotelName) {
    return {
      startDate: pilgrim.packageData.madinah.startDate,
      endDate: pilgrim.packageData.madinah.endDate
    };
  }
  return { startDate: '', endDate: '' };
};